import { useState, useRef, useCallback, Fragment } from 'react'
import Cropper from 'react-easy-crop'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { useMutation, gql, useReactiveVar } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'

import { setAuthData } from '../hooks/useAuth'
import { apolloAuthData } from '../services/apollo'

import { MAX_PROFILE_PHOTO_SIZE } from '../constants'

const PROFILE_PHOTO_URL = gql`
  mutation ProfilePhotoUrl(
    $clientMutationId: String
    $action: String!
    $fileName: String!
    $contentType: String
  ) {
    profilePhotoUrl(
      input: {
        clientMutationId: $clientMutationId
        action: $action
        fileName: $fileName
        contentType: $contentType
      }
    ) {
      url
      key
    }
  }
`

const UPDATE_USER_PROFILE_PIC = gql`
  mutation UpdateUser(
    $clientMutationId: String
    $id: ID!
    $profilePic: String!
  ) {
    updateUser(
      input: {
        clientMutationId: $clientMutationId
        id: $id
        profilePic: $profilePic
      }
    ) {
      user {
        id
        profilePic
      }
    }
  }
`

/**
 * Upload photo to S3 bucket.
 *
 * @param {Blob} imageBlob
 * @param {string} name - File name
 * @returns
 */
async function uploadFile(imageBlob, name = '', createProfilePhotoUrl) {
  // Get the presigned URL
  try {
    const response = await createProfilePhotoUrl({
      variables: {
        clientMutationId: uuidv4(),
        action: 'upload',
        fileName: name.replace(/@|\./g, '-'),
        contentType: imageBlob.type,
      },
    })

    const uploadURL = response?.data?.profilePhotoUrl?.url

    if (!uploadURL) {
      throw new Error('uploadURL missing from upload API response')
    }

    const uploadResponse = await fetch(uploadURL, {
      method: 'PUT',
      body: imageBlob,
      headers: {
        'Content-Type': imageBlob.type,
      },
    })

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed with status ${uploadResponse.status}`)
    }

    // Final URL for the user
    const photoURL = uploadURL.split('?')[0]

    return photoURL
  } catch (err) {
    console.log('uploadFile error', err)
    return false
  }
}

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    // image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
    image.src = url
  })

async function resizeImg(imageSrc, newWidth, newHeight, type) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  canvas.width = newWidth
  canvas.height = newHeight
  ctx.drawImage(
    imageSrc,
    0,
    0,
    imageSrc.width,
    imageSrc.height,
    0,
    0,
    newWidth,
    newHeight
  )

  return new Promise((resolve) => {
    if (type === 'jpeg') {
      canvas.toBlob(
        (file) => {
          resolve(file)
        },
        'image/jpeg',
        1
      )
    } else {
      canvas.toBlob((file) => {
        resolve(file)
      }, 'image/png')
    }
  })
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} imageSrc - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc)
  const type = imageSrc.includes('data:image/jpeg')
    ? 'jpeg'
    : imageSrc.includes('data:image/png')
    ? 'png'
    : 'jpeg'
  const canvas = document.createElement('canvas')

  const ctx = canvas.getContext('2d')

  const maxSize = Math.max(image.width, image.height)
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea
  canvas.height = safeArea

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2)
  ctx.translate(-safeArea / 2, -safeArea / 2)

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  )
  const data = ctx.getImageData(0, 0, safeArea, safeArea)

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  )

  // As Base64 string
  // return canvas.toDataURL('image/jpeg');

  // As a blob
  // return new Promise((resolve) => {
  //   if (type === 'jpeg') {
  //     canvas.toBlob(
  //       (file) => {
  //         resolve(file)
  //       },
  //       'image/jpeg',
  //       1
  //     )
  //   } else {
  //     canvas.toBlob((file) => {
  //       resolve(file)
  //     }, 'image/png')
  //   }
  // })

  // Resize image to 200x200
  return resizeImg(canvas, 200, 200, type)
}

const SelectProfilePhotoDialog = ({
  open = false,
  title = '',
  text = '',
  inProgressButtonText = 'Please wait...',
  setOpen = () => {},
  onClose = () => {},
  onConfirm = () => {},
}) => {
  // const auth = useAuth()
  const auth = useReactiveVar(apolloAuthData)
  const [selectedFileName, setSelectedFileName] = useState('')
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [img, setImg] = useState('')
  const [error, setError] = useState('')
  const [inProgress, setInProgress] = useState(false)
  const uploadInputRef = useRef(null)
  const [updateUserProfilePic] = useMutation(UPDATE_USER_PROFILE_PIC)
  const [createProfilePhotoUrl] = useMutation(PROFILE_PHOTO_URL)

  const handleCropChange = (c) => {
    setCrop(c)
  }
  const handleCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])
  const handleZoomChange = (z) => {
    setZoom(z)
  }

  const handleProfilePicUpload = (event) => {
    const file = event.target.files[0]

    // Don't do anything if file is undefined (when user cancels)
    if (!file) {
      return
    }

    // Save file name so we can display it.
    if (file && file.name && file.name.length > 0) {
      setSelectedFileName(file.name)
    }

    let reader = new FileReader()
    reader.onload = async (e) => {
      const { result } = e.target

      // Only support .png, .jpg, .jpeg
      if (
        !result.includes('data:image/jpeg') &&
        !result.includes('data:image/png')
      ) {
        setError(`File not supported.`)
        setImg('')
        return
      }

      // Check for supported file size
      if (result.length > MAX_PROFILE_PHOTO_SIZE) {
        setError(`File size too big!`)
        setImg('')
        return
      }

      // console.log(result)
      setImg(result)
    }

    reader.readAsDataURL(file)
  }

  const handleSelectClick = () => {
    uploadInputRef.current.click()
  }

  /**
   * Save photo to S3 and show it
   */
  const savePhoto = useCallback(
    async (email = '', img, onClose) => {
      // Don't do anything there's no image
      if (img.length === '') {
        return
      }

      if (email === '') {
        console.log('handleSavePhoto :: no email found')
        return
      }

      try {
        setInProgress(true)

        // Get cropped image
        const croppedImage = await getCroppedImg(img, croppedAreaPixels)

        // Upload to S3 bucket
        const uploadedPhotoURL = await uploadFile(
          croppedImage,
          email,
          createProfilePhotoUrl
        )

        if (!uploadedPhotoURL) {
          throw new Error('Profile photo upload failed')
        }

        // Update profilePic file name in the database
        const profilePic = `${email.replace(/@|\./g, '-')}.${
          croppedImage.type.split('/')[1]
        }`
        await updateUserProfilePic({
          variables: {
            clientMutationId: uuidv4(),
            id: auth.user.id,
            profilePic,
          },
        })

        // Create image URL for used through the app
        const profilePicURL = URL.createObjectURL(croppedImage)

        // Update our store
        setAuthData({
          ...apolloAuthData(),
          user: { ...auth.user, profilePicURL },
        })

        // Clean up our image
        setImg('')

        // Close the modal
        onClose(croppedImage)
      } catch (err) {
        toast.error('Unable to save the profile picture. Please try again.')
      } finally {
        setInProgress(false)
      }
    },
    [
      croppedAreaPixels,
      auth.user,
      createProfilePhotoUrl,
      updateUserProfilePic,
    ]
  )

  if (!open) {
    return null
  }

  return (
    <>
      <Transition appear show={open} as={Fragment} className="group">
        <Dialog as="div" className="relative" onClose={() => setOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    'group w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-sm transition-all',
                    inProgress && 'inProgress'
                  )}
                >
                  <Dialog.Title
                    as="h3"
                    className="mb-5 text-xl font-bold leading-none text-black-olive"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </div>

                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    id="profile-pic"
                    name="profile-pic"
                    onChange={handleProfilePicUpload}
                    className="absolute -z-[1] opacity-0"
                    ref={uploadInputRef}
                  />
                  <div className="flex items-center justify-center">
                    <button
                      className="rounded-full bg-bright-gray px-4 py-2 text-xs font-bold transition-colors hover:bg-flame hover:text-white"
                      onClick={handleSelectClick}
                    >
                      Select picture
                    </button>
                    <div className="ml-4 text-xs">
                      {selectedFileName.length > 0 ? (
                        <>
                          <span className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                            {selectedFileName
                              .split('.')
                              .slice(0, -1)
                              .join('.')
                              .trim()}
                          </span>
                          .
                          <span>
                            {selectedFileName.split('.').pop().trim()}
                          </span>
                        </>
                      ) : (
                        'No file chosen yet'
                      )}
                    </div>
                  </div>
                  <div className="relative -mx-6 mt-4 flex h-[300px] flex-col items-center justify-center bg-dark-gray text-white">
                    {inProgress && (
                      <div className="absolute top-0 left-0 z-50 h-full w-full bg-black-olive opacity-90">
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          Uploading picture... Please wait!
                        </span>
                      </div>
                    )}
                    {img ? (
                      <Cropper
                        image={img}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        onCropChange={handleCropChange}
                        onCropComplete={handleCropComplete}
                        onZoomChange={handleZoomChange}
                      />
                    ) : (
                      <>
                        {error.length > 0 && (
                          <span className="bg-yellow font-bold text-red-500">
                            {error}
                          </span>
                        )}
                        <div>
                          <strong>Accepted file:</strong> *.jpg or *.png (5MB
                          max.)
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-4 text-right">
                    <button
                      type="button"
                      className="rounded-full border-2 border-skin-button-accent px-6 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:bg-skin-button-accent group-[:not(.inProgress)]:hover:text-white group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ml-2 inline-flex items-center rounded-full border-2 border-skin-button-accent bg-skin-button-accent px-6 py-2 text-xs leading-none text-white transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={() =>
                        savePhoto(auth?.user?.email ?? '', img, onClose)
                      }
                    >
                      {inProgress ? inProgressButtonText : 'Done'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default SelectProfilePhotoDialog
